import React from 'react';
import { isManufacturerPharmacySelected } from '../../data/manufacturerPharmacies';

// §manufacturer-pharmacy — banner offering the manufacturer's own direct pharmacy
// as a one-click choice, so the patient doesn't have to know to search for
// "LillyDirect" or "NovoCare" by name.
//
// It sits ABOVE the pharmacy search rather than replacing it: this is a
// highlighted default, not the only option. A patient who wants their corner
// pharmacy just ignores it and searches as before.
//
// Selecting writes the same { pharmacyId, storeName, … } shape PharmacySelectStep
// emits, so downstream (`pharmacySelection: { mode: 'own' }`) is untouched.
//
// §2026-09-04: this is now rendered for a branded SKU on EITHER fork branch, not
// only after the patient has already picked "my own pharmacy" — the manufacturer
// pharmacy was invisible to anyone who left the default (ship) selected, which is
// most people. Because the manufacturer fills the prescription itself, choosing it
// necessarily moves the order onto the local path and off Fitpal's med retail
// price. `note` is where the parent states that consequence — never let the total
// change under the patient without a line of text saying it will.

const ManufacturerPharmacyBanner = ({ mfr, selected, onSelect, onClear, note, price, productName }) => {
  if (!mfr) return null;
  const isSelected = isManufacturerPharmacySelected(selected, mfr);
  const p = mfr.pharmacy;

  return (
    <div
      className={`rounded-2xl border-2 p-4 mb-3 transition-all ${
        isSelected ? 'border-[#2d8a5f] bg-[#f0faf4]' : 'border-[#2d8a5f]/40 bg-[#f7fcf9]'
      }`}
      data-testid={`mfr-pharmacy-banner-${mfr.key}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#2d8a5f]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2d8a5f]" />
            Manufacturer pharmacy
          </p>
          <h4 className="font-semibold text-gray-900 mt-1 leading-snug" data-testid="mfr-pharmacy-headline">
            {mfr.headline}
          </h4>
          <p className="text-sm text-gray-600 mt-1">{mfr.body}</p>
        </div>
        {isSelected && (
          <span
            className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-[#2d8a5f]"
            data-testid="mfr-pharmacy-selected-badge"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            Selected
          </span>
        )}
      </div>

      {/* §manufacturer-pricing: what the MANUFACTURER bills, on its own row and
          explicitly not charged here. This is the number a patient most wants
          before choosing this route, and its absence was the whole gap — the
          card showed our $59 and said nothing about the drug's cost.
          DISPLAY ONLY: never summed with anything, never sent to payment. */}
      {price && (
        <div className="mt-3 rounded-xl bg-white border border-gray-200 px-3 py-2" data-testid="mfr-price-block">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-gray-700">
              {price.label}
              {productName ? <span className="text-gray-400"> · {productName}</span> : null}
            </span>
            {price.amount && (
              <span className="text-base font-bold text-gray-900 flex-shrink-0" data-testid="mfr-price-amount">
                {price.amount}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{price.detail}</p>
        </div>
      )}

      {/* The exact record the prescription would be sent to — shown so the
          patient can see the address before committing, same as a search result. */}
      <div className="mt-3 rounded-xl bg-white border border-gray-200 px-3 py-2">
        <p className="text-sm font-semibold text-gray-900">{p.storeName}</p>
        <p className="text-xs text-gray-500">
          {[p.address1, p.city, p.state, p.zip].filter(Boolean).join(', ')}
        </p>
      </div>

      <div className="mt-3 flex items-center gap-3 flex-wrap">
        {isSelected ? (
          <button
            type="button"
            onClick={onClear}
            data-testid="mfr-pharmacy-clear"
            className="text-sm text-gray-500 underline underline-offset-2 hover:text-gray-700"
          >
            Choose a different pharmacy instead
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onSelect(p)}
            data-testid="mfr-pharmacy-select"
            className="rounded-xl bg-[#2d8a5f] px-4 py-2 text-sm font-semibold text-white hover:bg-[#236b4a]"
          >
            Send it to {p.storeName}
          </button>
        )}
        <a
          href={mfr.learnMoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="mfr-pharmacy-learn-more"
          className="text-sm text-[#2d8a5f] underline underline-offset-2 hover:text-[#236b4a]"
        >
          Learn about {mfr.brand}
        </a>
      </div>

      {/* What choosing this does to the charge. Only rendered when it would
          actually change — see the §2026-09-04 note above. */}
      {note && !isSelected && (
        <p className="text-xs text-gray-500 mt-2" data-testid="mfr-pharmacy-note">
          {note}
        </p>
      )}
    </div>
  );
};

export default ManufacturerPharmacyBanner;
